import DataSource from './data-source/data-source';
import performance from './performance/performance';
import { env } from './env';
import ResourceDataItem from './data-items/resource/resource-item';
import BiLoadPhaseDataItem from './data-items/app-load/bi-load-phase-item';
import DurationDataItem from './data-items/duration/duration-item';
import BiErrorItem from './data-items/common/bi-error-item';
import AppLoadingPhaseItem from './data-items/app-load/app-loading-phase-item';
import PrefixedAppLoadingPhaseItem from './data-items/app-load/prefixed-app-loading-phase-item';
import BiLoadFinishDataItem from './data-items/app-load/bi-load-finish-item';
import BiInteractionStartDataItem from './data-items/interaction/bi-interaction-start-item';
import BiInteractionEndDataItem from './data-items/interaction/bi-interaction-end-item';
import sessionManager from './session-manager/session-manager';
import LoadingPhases, {
  CODE_PARSING_PHASE_NAME,
} from './loading-phases/loading-phases';

const callHook = (hook, hookArgs) => {
  if (hook) {
    return hook(hookArgs);
  }
};

/**
 * Base Logger
 * @param appName
 * @param appVersion
 * @param reporter
 *
 * Params object properties:
 * @param baseUrl
 * @param appId
 * @param disableAutoLoadFinish
 * @param disableAutoPhaseFinish
 * @param interactionTimeout
 * @param timeoutHook
 * @param startHook
 * @param endHook
 * Before adding new parameters to the reported events, please make sure the BI schema supports them:
 * http://bo.wix.com/bi-catalog-webapp/#/sources/72
 */
export default class BaseLogger {
  constructor(appName, appVersion, reporter, params) {
    const defaultParams = {
      appId: null,
      metasiteId: null,
      sessionId: sessionManager.getSessionId(),
      disableAutoLoadFinish: false,
      disableAutoPhaseFinish: false,
      interactionTimeout: null,
      timeoutHook: null,
      startHook: null,
      endHook: null,
    };

    const paramsWithDefaults = Object.assign({}, defaultParams, params);
    this.appName = appName;
    this.appVersion = appVersion;
    this.appId = paramsWithDefaults.appId;
    this.metasiteId = paramsWithDefaults.metasiteId;
    this.reporter = reporter;

    this.params = { sessionId: paramsWithDefaults.sessionId };
    this.disableAutoLoadFinish = paramsWithDefaults.disableAutoLoadFinish;

    this.disableAutoPhaseFinish = paramsWithDefaults.disableAutoPhaseFinish;
    this.loadingPhases = new LoadingPhases(appName);
    this.dataSource = new DataSource({ appName });

    this._outgoingInteractions = {};
    this.interactionTimeout = paramsWithDefaults.interactionTimeout;
    this.timeoutHook = paramsWithDefaults.timeoutHook;
    this.startHook = paramsWithDefaults.startHook;
    this.endHook = paramsWithDefaults.endHook;
  }

  _report(dataSource) {
    return this.reporter.report(dataSource.mergeItems());
  }

  getAppName() {
    return this.appName;
  }

  getAppVersion() {
    return this.appVersion;
  }

  getReporter() {
    return this.reporter;
  }

  getParam(paramName) {
    return this.params[paramName];
  }

  _isDisableAutoLoadFinish() {
    return this.disableAutoLoadFinish;
  }

  get sessionId() {
    return this.getParam('sessionId');
  }

  isDisableAutoLoadFinish() {
    return this._isDisableAutoLoadFinish();
  }

  reportNetworkAnalysis() {
    const resources = performance.getResources();
    setTimeout(() => {
      try {
        const resourceDataItem = new ResourceDataItem(this.sessionId);
        const analysis = resourceDataItem.performNetworkAnalysis(resources);
        if (!analysis) {
          return;
        }
        const dataSource = this.dataSource
          .clone()
          .addItem(resourceDataItem)
          .addItem(analysis);
        this.reporter.useBaseUrl();
        this._report(dataSource);
      } catch (e) {
        console.error('[fedops] Failed to report resource metrics', e);
      }
    }, 1000);
  }

  /**
   * Final phase.
   * Call this method when your application finishes loading and after all custom phases.
   */
  appLoaded() {
    if (this._appLoadedCalled) {
      return;
    }

    BaseLogger._clearLoadTimeout();
    BaseLogger._markAndMeasureAppLoad();
    this._appLoadedCalled = true;

    this._sendLastAppLoadPhaseIfNeeded();
    const dataSource = this._getAppLoadedDataSource();

    this.reportNetworkAnalysis();
    return this._report(dataSource);
  }

  static _clearLoadTimeout() {
    if (
      env() &&
      env().fedops &&
      typeof env().fedops.clearLoadTimeout === 'function'
    ) {
      env().fedops.clearLoadTimeout();
    }
  }

  _getAppLoadedDataSource() {
    return this.dataSource
      .clone()
      .addItem(new BiLoadFinishDataItem(this.sessionId))
      .addItem(new DurationDataItem(this.appName).setFirstRequestDuration());
  }

  static _markAndMeasureAppLoad() {
    performance.mark(`[fedops] app-loaded`);
    performance.measure(
      '[fedops] app-loaded',
      'navigationStart',
      '[fedops] app-loaded',
    );
  }

  _sendLastAppLoadPhaseIfNeeded() {
    const appLoadingPhase = this.loadingPhases.getNextPhaseToReport();
    const shouldReportLastPhase =
      !this.disableAutoPhaseFinish &&
      appLoadingPhase &&
      appLoadingPhase.name !== CODE_PARSING_PHASE_NAME;

    if (shouldReportLastPhase) {
      this.reporter.useBaseUrl();
      this._report(
        this.dataSource
          .clone()
          .addItem(new PrefixedAppLoadingPhaseItem(appLoadingPhase))
          .addItem(new BiLoadPhaseDataItem(this.sessionId)),
      );
    }
  }

  /**
   * Custom interaction start (http://bo.wix.com/bi-catalog-webapp/#/sources/72/events/15)
   * @param {String} interactionName  interaction name
   */
  interactionStarted(
    interactionName,
    { interactionTimeout, startHook, timeoutHook } = {},
  ) {
    performance.mark(`${interactionName} started`);
    const scheduleTimeoutEvent = () => {
      if (interactionTimeout || this.interactionTimeout) {
        return setTimeout(() => {
          const dataSource = this.dataSource
            .clone()
            .addItem({ interactionName })
            .addItem({ errorType: 'timeout' })
            .addItem(new BiErrorItem(this.sessionId));

          this._report(dataSource);
          callHook(timeoutHook || this.timeoutHook, {
            name: interactionName,
            timeout: interactionTimeout || this.interactionTimeout,
          });
        }, interactionTimeout || this.interactionTimeout);
      }
    };

    const dataSource = this.dataSource
      .clone()
      .addItem({ name: interactionName })
      .addItem(new BiInteractionStartDataItem(this.sessionId));

    this._outgoingInteractions[interactionName] = {
      timestamp: performance.now(),
      timeout: interactionTimeout || this.interactionTimeout,
    };
    this.reporter.useBaseUrl();
    this._report(dataSource);
    callHook(startHook || this.startHook, { name: interactionName });

    const timeoutId = scheduleTimeoutEvent();
    return {
      timeoutId,
    };
  }

  /**
   * Custom interaction end (http://bo.wix.com/bi-catalog-webapp/#/sources/72/events/16)
   * @param {String} interactionName  interaction name
   */
  interactionEnded(interactionName, { timeoutId, endHook } = {}) {
    performance.mark(`${interactionName} ended`);
    performance.measure(
      `${interactionName} duration`,
      `${interactionName} started`,
      `${interactionName} ended`,
    );

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const actualEnv = env();
    const getInteractionFromWindowIfPresent = () =>
      actualEnv.fedops &&
      actualEnv.fedops.apps &&
      actualEnv.fedops.apps[this.appName] &&
      actualEnv.fedops.apps[this.appName].interactions &&
      actualEnv.fedops.apps[this.appName].interactions[interactionName];

    const interaction =
      this._outgoingInteractions[interactionName] ||
      getInteractionFromWindowIfPresent();
    const duration = interaction
      ? Math.floor(performance.now() - interaction.timestamp)
      : '';

    callHook(endHook || this.endHook, {
      name: interactionName,
      duration,
      timeout:
        this._outgoingInteractions[interactionName] &&
        this._outgoingInteractions[interactionName].timeout,
    });

    const dataSource = this.dataSource
      .clone()
      .addItem({ name: interactionName })
      .addItem({ duration })
      .addItem(new BiInteractionEndDataItem(this.sessionId));

    delete this._outgoingInteractions[interactionName];

    this.reporter.useBaseUrl();
    this._report(dataSource);
  }

  /**
   * Start of an app loading phase, reports end of previous phase if configured
   * Sends phase event (http://bo.wix.com/bi-catalog-webapp/#/sources/72/events/13)
   * @param name of the step
   */
  appLoadingPhaseStart(name) {
    if (this._appLoadedCalled) {
      return;
    }

    this.loadingPhases.saveAppLoadingPhase({ name });
    const previousPhase = this.loadingPhases.getPhasePreviousTo(name);

    if (
      !this.disableAutoPhaseFinish ||
      previousPhase.name === CODE_PARSING_PHASE_NAME
    ) {
      const loadingPhaseDataItem = new PrefixedAppLoadingPhaseItem(
        previousPhase,
      );
      const loadingPhaseBiItem = new BiLoadPhaseDataItem(this.sessionId);

      const dataSource = this.dataSource
        .clone()
        .addItem(loadingPhaseDataItem)
        .addItem(loadingPhaseBiItem);

      this.reporter.useBaseUrl();
      return this._report(dataSource);
    }
  }

  /**
   * End of an app loading phase, reports end of previous phase
   * Sends phase event (http://bo.wix.com/bi-catalog-webapp/#/sources/72/events/13)
   * @param name of the step
   */
  appLoadingPhaseFinish(name) {
    if (this._appLoadedCalled) {
      return;
    }

    if (!this.disableAutoPhaseFinish) {
      throw new Error(
        'To use appLoadingPhaseFinish you must create the logger with the "disableAutoPhaseFinish: true" param',
      );
    }

    const appLoadingPhase = this.loadingPhases.getAppLoadingPhaseData(name);
    if (!appLoadingPhase) {
      throw new Error(
        `Cannot report end of a phase that wasn't started. Phase ${name} doesn't exist`,
      );
    }

    const loadingPhaseDataItem = new AppLoadingPhaseItem(appLoadingPhase);
    const loadingPhaseBiItem = new BiLoadPhaseDataItem(this.sessionId);

    const dataSource = this.dataSource
      .clone()
      .addItem(loadingPhaseDataItem)
      .addItem(loadingPhaseBiItem);

    this.reporter.useBaseUrl();
    return this._report(dataSource);
  }
}
